// Verleih von Geräten (Tabelle rental_booking): Verfügbarkeit je Tag, Buchung zum Auftrag, Ausgabe und Rücknahme
// mit Zustand. Leihartikel sind bestandsgeführt (gezählt oder mit Seriennummer); ausgegebene Stücke stehen am
// Standort „Verleih beim Kunden“. Die Leihgebühr ist eine Gebührenzeile des Auftrags und wird mit ihm berechnet.
// Stückzahlen in Tausendstel (2 Garnituren = 2000), Geld in Cent.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zCustomerRef, zDay, zLocationRef, zNote, zUnitQty, zUnitQtyOrZero } from './common'

/** Funktionsschalter „Leihartikel“ (Branchenprofil): ohne ihn gibt es diese Aktionen für den Betrieb nicht */
const FEATURE = 'rental'

const PERMISSIONS = { read: 'sales.rental.read', write: 'sales.rental.write' } as const

export const RENTAL_STATUSES = ['requested', 'confirmed', 'out', 'returned', 'cancelled'] as const

const zRentalTransfer = z.object({ id: zId, number: z.string().nullable(), status: z.string(), statusLabel: z.string() })

export const zRentalBooking = z.object({
  id: zId,
  version: z.int(),
  status: z.enum(RENTAL_STATUSES),
  statusLabel: z.string(),
  item: z.object({ id: zId, sku: z.string(), name: z.string(), unit: z.string(), serialTracked: z.boolean() }),
  /** Standort, an dem das Gerät zu Hause ist */
  location: zLocationRef,
  customer: zCustomerRef.nullable(),
  fromDate: z.string(),
  toDate: z.string(),
  /** Miettage (beide Tage eingeschlossen) */
  days: z.int(),
  rentalQty: z.int(),
  returnedQty: z.int(),
  damagedQty: z.int(),
  /** ausgegeben, aber nicht zurückgekommen (erst nach der Rücknahme gesetzt) */
  missingQty: z.int(),
  serialNos: z.array(z.string()),
  /** Kaution in Cent – vermerkt, nicht gebucht */
  securityDepositCents: z.int(),
  /** Zustand bei der Rücknahme */
  condition: z.string().nullable(),
  note: z.string().nullable(),
  orderId: zId.nullable(),
  orderNumber: z.string().nullable(),
  handedOverAt: z.date().nullable(),
  returnedAt: z.date().nullable(),
  // ---- additiv (Filiale, docs/veranstaltungen/SPEZIFIKATION.md §10) ----
  /** Ausgabe und Rücknahme in diesem Standort (Filiale, Abholung dort); null = am Heimatstandort */
  targetLocation: zLocationRef.nullable().optional(),
  /** Umlagerung Heimatstandort → Ausgabestandort bzw. zurück (Nummer, Stand); null = keine (oder Aufgabe fürs Lager) */
  transferOut: zRentalTransfer.nullable().optional(),
  transferBack: zRentalTransfer.nullable().optional(),
})

const zAvailabilityDay = z.object({
  date: z.string(),
  /** an diesem Tag gebunden (gebucht oder beim Kunden), Stück in Tausendstel */
  bookedQty: z.int(),
  freeQty: z.int(),
})

export const rentalAvailability = defineContract({
  name: 'sales.rental.availability',
  title: 'Verfügbarkeit von Leihartikeln anzeigen',
  description: 'Je Leihartikel und Tag: Bestand, gebuchte und freie Stücke.',
  llm: {
    description:
      'Liefert für einen Zeitraum (höchstens 62 Tage) je Leihartikel den Gesamtbestand (zu Hause und beim Kunden) und ' +
      'je Tag die gebundenen und freien Stücke in Tausendstel (2 Garnituren = 2000). Gebunden ist ein Gerät von ' +
      'Beginn bis Ende jeder Buchung, die nicht storniert oder zurückgenommen ist. Verwenden vor einer Buchung: ' +
      '„Ist der Kühlanhänger am Donnerstag frei?“. Gebucht wird mit sales.rental.book.',
    keywords: ['Leihartikel frei', 'Verfügbarkeit', 'Verleih', 'Kühlanhänger', 'Zapfanlage', 'Biertischgarnitur', 'Verleihkalender'],
  },
  input: z.strictObject({
    fromDate: zDay,
    toDate: zDay,
    itemIds: z.array(zId).max(100).optional(),
    locationId: zId.optional(),
  }),
  output: z.object({
    fromDate: z.string(),
    toDate: z.string(),
    items: z.array(
      z.object({
        itemId: zId,
        sku: z.string(),
        name: z.string(),
        unit: z.string(),
        serialTracked: z.boolean(),
        /** Gesamtbestand in Tausendstel */
        totalQty: z.int(),
        /** kleinste freie Stückzahl im Zeitraum */
        minFreeQty: z.int(),
        days: z.array(zAvailabilityDay),
      }),
    ),
  }),
  permission: PERMISSIONS.read,
  feature: FEATURE,
  risk: 'read',
  scope: 'any',
})

export const rentalList = defineContract({
  name: 'sales.rental.list',
  title: 'Leihbuchungen anzeigen',
  description: 'Buchungen von Leihartikeln mit Zeitraum, Kunde, Zustand und Rückgabe.',
  llm: {
    description:
      'Listet Leihbuchungen: Artikel, Kunde, Zeitraum, Stück (Tausendstel), Zustand (Gebucht, Beim Kunden, Zurück, ' +
      'Storniert), Seriennummern, Rückgabe mit Beschädigungen und Fehlmengen. Filter: Zustand, Kunde, Artikel, ' +
      'Auftrag, Zeitraum (überlappend).',
    keywords: ['Leihbuchungen', 'Verleih', 'wer hat', 'ausgeliehen', 'Rückgabe offen'],
  },
  input: z.strictObject({
    status: z.array(z.enum(RENTAL_STATUSES)).max(5).optional(),
    partnerId: zId.optional(),
    itemId: zId.optional(),
    orderId: zId.optional(),
    fromDate: zDay.optional(),
    toDate: zDay.optional(),
    limit: z.int().min(1).max(500).default(200),
  }),
  output: z.object({ rows: z.array(zRentalBooking) }),
  permission: PERMISSIONS.read,
  feature: FEATURE,
  risk: 'read',
  scope: 'any',
})

export const rentalBook = defineContract({
  name: 'sales.rental.book',
  title: 'Leihartikel buchen',
  description: 'Bucht Leihartikel für einen Zeitraum – auf Wunsch zum Auftrag mit Leihgebühr.',
  llm: {
    description:
      'Bucht einen Leihartikel für einen Kunden von `fromDate` bis `toDate` (beide Tage gebunden). `rentalQty` in ' +
      'Tausendstel (10 Garnituren = 10000). Abgelehnt, wenn an einem Tag des Zeitraums nicht genug Stücke frei sind ' +
      '(keine Doppelbuchung). Mit `orderId` hängt die Buchung am Auftrag: Kunde und Standort kommen von dort, und ' +
      'der Auftrag erhält eine Gebührenzeile „Leihgebühr …“ – zum Preis der Preisfindung für den Leihartikel oder zu ' +
      '`feeUnitPriceCents` (Cent je Stück in der Preisbasis des Auftrags; 0 = ohne Gebühr). Ohne Auftrag braucht es ' +
      '`partnerId` und `locationId`. Mit `targetLocationId` (Filiale) wird dort ausgegeben und zurückgenommen; die Umlagerung ' +
      'vom Heimatstandort zum ersten Miettag wird angefordert (zurück nach der Rücknahme). Ausgegeben wird mit sales.rental.handOver.',
    keywords: ['Leihartikel buchen', 'reservieren', 'Verleih', 'Zapfanlage buchen', 'Kühlanhänger reservieren', 'Leihgebühr'],
  },
  input: z.strictObject({
    itemId: zId,
    fromDate: zDay,
    toDate: zDay,
    rentalQty: zUnitQty.multipleOf(1000, { error: 'Leihartikel werden in ganzen Stücken gebucht.' }),
    orderId: zId.nullish(),
    partnerId: zId.nullish(),
    locationId: zId.nullish(),
    /** additiv (Filiale, §10): Ausgabe und Rücknahme in diesem Lager oder Markt – Umlagerung vom Heimatstandort hin und zurück */
    targetLocationId: zId.nullish(),
    feeUnitPriceCents: z.int({ error: 'Bitte einen Betrag eingeben.' }).min(0, { error: 'Die Gebühr darf nicht negativ sein.' }).max(100_000_000).nullish(),
    securityDepositCents: z.int({ error: 'Bitte einen Betrag eingeben.' }).min(0, { error: 'Die Kaution darf nicht negativ sein.' }).max(100_000_000).default(0),
    note: zNote,
  }),
  output: zRentalBooking,
  permission: PERMISSIONS.write,
  feature: FEATURE,
  risk: 'write',
  scope: 'record',
  effects: { reversible: true },
})

export const rentalCancel = defineContract({
  name: 'sales.rental.cancel',
  title: 'Leihbuchung stornieren',
  description: 'Storniert eine Buchung, solange nichts ausgegeben ist – die Stücke sind wieder frei.',
  llm: {
    description: 'Storniert eine Leihbuchung im Zustand „Gebucht“. Die Stücke sind für den Zeitraum wieder frei; hängt die Buchung an einem noch nicht bestätigten Auftrag, entfällt dessen Gebührenzeile. Nach der Ausgabe: sales.rental.takeBack.',
    keywords: ['Leihbuchung stornieren', 'Verleih absagen'],
  },
  input: z.strictObject({ id: zId }),
  output: zRentalBooking,
  permission: PERMISSIONS.write,
  feature: FEATURE,
  risk: 'destructive',
  scope: 'record',
})

export const rentalHandOver = defineContract({
  name: 'sales.rental.handOver',
  title: 'Leihartikel ausgeben',
  description: 'Übergibt gebuchte Leihartikel an den Kunden: Der Bestand wechselt an den Standort „Verleih beim Kunden“.',
  llm: {
    description:
      'Gibt eine gebuchte Leihbuchung aus. Der Bestand der Stücke geht vom Heimatstandort an den Verleih-Standort; ' +
      'bei Artikeln mit Seriennummer nennt `serialNos` genau so viele Nummern wie Stücke. Danach ist die Buchung ' +
      '„Beim Kunden“. Zurück mit sales.rental.takeBack.',
    keywords: ['Leihartikel ausgeben', 'übergeben', 'Verleih Ausgabe', 'abholen lassen'],
  },
  input: z.strictObject({ id: zId, serialNos: z.array(z.string().trim().min(1).max(80)).max(200).optional(), note: zNote }),
  output: zRentalBooking,
  permission: PERMISSIONS.write,
  feature: FEATURE,
  risk: 'write',
  scope: 'record',
})

export const rentalTakeBack = defineContract({
  name: 'sales.rental.takeBack',
  title: 'Leihartikel zurücknehmen',
  description: 'Nimmt ausgegebene Leihartikel zurück, hält Zustand, Beschädigungen und Fehlmengen fest.',
  llm: {
    description:
      'Nimmt eine Leihbuchung im Zustand „Beim Kunden“ zurück. `returnedQty` = zurückgekommene Stücke (Tausendstel), ' +
      'davon `damagedQty` beschädigt (gehen in den Sperrbestand des Heimatstandorts), `condition` beschreibt den ' +
      'Zustand. Was nicht zurückkommt, bleibt als Fehlmenge am Verleih-Standort stehen und ist an der Buchung ' +
      'ausgewiesen – berechnet wird es über das Modul Finanzen. Artikel mit Seriennummer kommen vollständig zurück.',
    keywords: ['Leihartikel zurück', 'Rückgabe', 'Verleih Rücknahme', 'beschädigt', 'fehlt'],
  },
  input: z.strictObject({
    id: zId,
    returnedQty: zUnitQtyOrZero.multipleOf(1000, { error: 'Leihartikel werden in ganzen Stücken gezählt.' }),
    damagedQty: zUnitQtyOrZero.multipleOf(1000, { error: 'Leihartikel werden in ganzen Stücken gezählt.' }).default(0),
    condition: z.string().trim().max(500).nullish(),
  }),
  output: zRentalBooking,
  permission: PERMISSIONS.write,
  feature: FEATURE,
  risk: 'write',
  scope: 'record',
})
